/**
 * AlleMailsTab — „Alle Mails"-Tab für das ForderungsTagesbriefing.
 *
 * Zeigt die letzten eingehenden Mails (aus /api/forderung-tagesbericht →
 * alle_mails). Je Mail:
 *   • Absender / Betreff / Snippet / Datum + Gelesen-Badge + Anhang-Indikator
 *   • interner Kommentar (POST /api/mail-kommentar) + bestehende Kommentare
 *   • Absender-Umschalter (forderungen@ / info@) + 3 Antwort-Buttons
 *     „Freundlich" / „Eskalieren" / „Ausbuchen" (POST /api/antwort-generieren,
 *     als_entwurf_speichern:true) → editierbare Vorschau → „Freigeben & senden"
 *     (POST /api/email-draft-send { id, userId }).
 *
 * Self-contained: nutzt fetch mit Bearer-Token (props.authToken) — analog zum
 * Auth-Vorgehen der Elternkomponente. Styling: Tailwind, Brand-Rot #9B1C1C.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2,
  Send,
  Paperclip,
  Mail,
  MessageSquarePlus,
  Sparkles,
  Clock,
} from 'lucide-react';

const BRAND = '#9B1C1C';

export interface AlleMailKommentar {
  kommentar: string;
  erstellt_am?: string | null;
}

export interface AlleMail {
  id: string;
  gmail_message_id?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  subject?: string | null;
  snippet?: string | null;
  date?: string | null;
  is_read?: boolean;
  has_attachments?: boolean;
  handlungsbedarf?: boolean;
  kommentare?: AlleMailKommentar[];
}

interface AlleMailsTabProps {
  mails: AlleMail[];
  userId: string;
  authToken: string;
}

type Option = 'freundlich' | 'eskalieren' | 'ausbuchen';
type Absender = 'forderungen' | 'info';

interface GenerierterEntwurf {
  draftId: string | null;
  subject: string;
  bodyText: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function datumZeit(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Einzelne Mail-Karte ─────────────────────────────────────────────────────

function MailKarte({
  mail,
  authToken,
  userId,
}: {
  mail: AlleMail;
  authToken: string;
  userId: string;
}) {
  const [kommentare, setKommentare] = useState<AlleMailKommentar[]>(mail.kommentare ?? []);
  const [kommentarText, setKommentarText] = useState('');
  const [speichereKommentar, setSpeichereKommentar] = useState(false);

  const [absender, setAbsender] = useState<Absender>('forderungen');
  const [generiere, setGeneriere] = useState<Option | null>(null);
  const [entwurf, setEntwurf] = useState<GenerierterEntwurf | null>(null);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [sende, setSende] = useState(false);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  // Kommentar speichern → POST /api/mail-kommentar
  const handleKommentarSpeichern = async () => {
    const wert = kommentarText.trim();
    if (!wert) {
      toast.error('Bitte einen Kommentar eingeben');
      return;
    }
    setSpeichereKommentar(true);
    try {
      const res = await fetch('/api/mail-kommentar', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          email_archive_id: mail.id,
          gmail_message_id: mail.gmail_message_id ?? undefined,
          kommentar: wert,
        }),
      });
      const json = (await res.json().catch(() => null)) as
        | { ok?: boolean; error?: string }
        | null;
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setKommentare((prev) => [
        ...prev,
        { kommentar: wert, erstellt_am: new Date().toISOString() },
      ]);
      setKommentarText('');
      toast.success('Kommentar gespeichert');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fehler beim Speichern';
      toast.error(`Kommentar konnte nicht gespeichert werden: ${msg}`);
    } finally {
      setSpeichereKommentar(false);
    }
  };

  // Antwort generieren → POST /api/antwort-generieren (als_entwurf_speichern:true)
  const handleGenerieren = async (option: Option) => {
    setGeneriere(option);
    try {
      const res = await fetch('/api/antwort-generieren', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          mail_id: mail.id,
          option,
          absender,
          als_entwurf_speichern: true,
        }),
      });
      const json = (await res.json().catch(() => null)) as
        | { ok?: boolean; subject?: string; body_text?: string; draft_id?: string | null; error?: string }
        | null;
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      const neuerEntwurf: GenerierterEntwurf = {
        draftId: json.draft_id ?? null,
        subject: json.subject ?? '',
        bodyText: json.body_text ?? '',
      };
      setEntwurf(neuerEntwurf);
      setBetreff(neuerEntwurf.subject);
      setText(neuerEntwurf.bodyText);
      toast.success('Antwort generiert');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fehler beim Generieren';
      toast.error(`Antwort konnte nicht generiert werden: ${msg}`);
    } finally {
      setGeneriere(null);
    }
  };

  // Freigeben & senden → POST /api/email-draft-send { id, userId }
  const handleSenden = async () => {
    if (!entwurf?.draftId) {
      toast.error('Kein gespeicherter Entwurf zum Senden');
      return;
    }
    setSende(true);
    try {
      const res = await fetch('/api/email-draft-send', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id: entwurf.draftId, userId }),
      });
      const json = (await res.json().catch(() => null)) as
        | { ok?: boolean; error?: string }
        | null;
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      toast.success('Gesendet');
      setEntwurf(null);
      setBetreff('');
      setText('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Senden fehlgeschlagen';
      toast.error(`Senden fehlgeschlagen: ${msg}`);
    } finally {
      setSende(false);
    }
  };

  const gelesen = mail.is_read === true;

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      {/* Kopf: Absender / Betreff / Badges */}
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900">
            {mail.subject || 'Ohne Betreff'}
          </div>
          <div className="truncate text-xs text-gray-500">
            {mail.from_name || mail.from_email || 'Unbekannt'}
            {mail.from_name && mail.from_email ? ` · ${mail.from_email}` : ''}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {mail.has_attachments && (
            <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
              <Paperclip className="h-3 w-3" />
            </span>
          )}
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              gelesen ? 'bg-gray-100 text-gray-500' : 'text-white'
            }`}
            style={gelesen ? undefined : { backgroundColor: BRAND }}
          >
            {gelesen ? 'Gelesen' : 'Ungelesen'}
          </span>
        </div>
      </div>

      {mail.snippet && (
        <p className="mb-2 line-clamp-2 whitespace-pre-wrap text-sm text-gray-600">
          {mail.snippet}
        </p>
      )}

      <div className="mb-3 flex items-center gap-1 text-xs text-gray-400">
        <Clock className="h-3 w-3" />
        {datumZeit(mail.date) || 'Kein Datum'}
      </div>

      {/* Bestehende Kommentare */}
      {kommentare.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {kommentare.map((k, i) => (
            <div
              key={i}
              className="rounded-lg bg-gray-50 p-2 text-sm text-gray-700"
            >
              <p className="whitespace-pre-wrap">{k.kommentar}</p>
              {k.erstellt_am && (
                <span className="text-[11px] text-gray-400">
                  {datumZeit(k.erstellt_am)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Kommentar-Eingabe */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={kommentarText}
          onChange={(e) => setKommentarText(e.target.value)}
          placeholder="Interner Kommentar …"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-red-100"
        />
        <button
          type="button"
          onClick={handleKommentarSpeichern}
          disabled={speichereKommentar}
          className="flex h-fit shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
        >
          {speichereKommentar ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MessageSquarePlus className="h-4 w-4" />
          )}
          Kommentar speichern
        </button>
      </div>

      {/* Antwort-Generator: Absender-Umschalter + 3 Buttons */}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
        <div className="flex overflow-hidden rounded-lg border border-gray-300 text-xs">
          <button
            type="button"
            onClick={() => setAbsender('forderungen')}
            className={`px-2.5 py-1.5 font-medium transition ${
              absender === 'forderungen' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            style={absender === 'forderungen' ? { backgroundColor: BRAND } : undefined}
          >
            forderungen@
          </button>
          <button
            type="button"
            onClick={() => setAbsender('info')}
            className={`px-2.5 py-1.5 font-medium transition ${
              absender === 'info' ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            style={absender === 'info' ? { backgroundColor: BRAND } : undefined}
          >
            info@
          </button>
        </div>

        {(['freundlich', 'eskalieren', 'ausbuchen'] as Option[]).map((opt) => {
          const label =
            opt === 'freundlich' ? 'Freundlich' : opt === 'eskalieren' ? 'Eskalieren' : 'Ausbuchen';
          const busy = generiere === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => handleGenerieren(opt)}
              disabled={generiere !== null}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {label}
            </button>
          );
        })}
      </div>

      {/* Editierbare Vorschau + Senden */}
      {entwurf && (
        <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Betreff</label>
            <input
              type="text"
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-red-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Antworttext</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              className="w-full resize-y rounded-lg border border-gray-300 p-3 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-red-100"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">
              {entwurf.draftId
                ? 'Als Entwurf gespeichert (ready_for_review).'
                : 'Kein Entwurf gespeichert — Senden nicht möglich.'}
            </span>
            <button
              type="button"
              onClick={handleSenden}
              disabled={sende || !entwurf.draftId}
              className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white transition disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {sende ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Freigeben &amp; senden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab ─────────────────────────────────────────────────────────────────────

export default function AlleMailsTab({ mails, userId, authToken }: AlleMailsTabProps) {
  if (!mails || mails.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-400">
        Keine eingehenden Mails vorhanden.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4" style={{ color: BRAND }} />
        <h3 className="text-sm font-semibold text-gray-900">Alle Mails</h3>
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
          style={{ backgroundColor: BRAND }}
        >
          {mails.length}
        </span>
      </div>
      {mails.map((m) => (
        <MailKarte key={m.id} mail={m} authToken={authToken} userId={userId} />
      ))}
    </div>
  );
}
